"use client";

import { useState, useEffect, useTransition } from "react";
import { usePathname } from "next/navigation";
import { browserClient } from "@/lib/supabase/clients/browser-client";
import { requestEmailLink, hasEmailOnFile } from "@/lib/auth/auth-actions";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Mail, CheckCircle } from "lucide-react";
import { User } from "@supabase/supabase-js";
import posthog from "posthog-js";

function needsEmail(user: User | null): boolean {
  if (!user) return false;
  // Anonymous visitors have no email because they haven't verified anything —
  // the nurture prompt is only for real (phone-verified, wa-* placeholder)
  // accounts, never for anon sessions.
  if (user.is_anonymous) return false;
  const email = user.email;
  return !email || email.startsWith("wa-");
}

export function EmailBanner() {
  const [user, setUser] = useState<User | null>(null);
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const pathname = usePathname();
  // /editar is an owner self-serve surface (carrier editing their own profile),
  // not a lead-nurture surface — never prompt for a lead email there.
  const onEditRoute = pathname?.endsWith("/editar") ?? false;

  // needsEmail() is a cheap client-side gate on the auth email; when it passes
  // we still confirm server-side that no nurture email is on file (saved to
  // public.users via the auth modal, which needsEmail can't see) before showing.
  const resolveShow = async (u: User | null) => {
    if (!needsEmail(u)) {
      setShow(false);
      return;
    }
    const onFile = await hasEmailOnFile();
    setShow(!onFile);
  };

  useEffect(() => {
    browserClient.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
      resolveShow(user);
    });

    const {
      data: { subscription },
    } = browserClient.auth.onAuthStateChange(async (_event, session) => {
      if (!session) {
        setUser(null);
        setShow(false);
        return;
      }
      // Always fetch from DB — session JWT may have stale email
      const { data: { user: freshUser } } = await browserClient.auth.getUser();
      setUser(freshUser);
      resolveShow(freshUser);
    });

    return () => subscription.unsubscribe();
  }, []);

  // When the auth modal sends a verification link, it closes and hands the
  // "Revisa tu correo" confirmation to this banner (it owns that sent-state UI).
  useEffect(() => {
    const onLinkSent = (e: Event) => {
      const detail = (e as CustomEvent<{ email?: string }>).detail;
      if (detail?.email) setEmail(detail.email);
      setSent(true);
      setShow(true);
    };
    window.addEventListener("jaro:email-link-sent", onLinkSent);
    return () => window.removeEventListener("jaro:email-link-sent", onLinkSent);
  }, []);

  if (onEditRoute || !show) return null;

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = () => {
    setError(null);
    posthog.capture("email_banner_submitted", { email_domain: email.split("@")[1] });
    startTransition(async () => {
      const result = await requestEmailLink(email);
      if ("error" in result && result.error) {
        setError(result.error);
      } else {
        setSent(true);
      }
    });
  };

  return (
    <div className="bg-orange-50 border-b border-orange-100">
      <div className="container mx-auto px-4 py-3">
        {sent ? (
          <div className="flex items-center justify-center gap-2 text-sm text-orange-800">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span>
              Revisa tu correo <strong>{email}</strong> y haz clic en el enlace
              para confirmar.
            </span>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <div className="flex items-center gap-2 text-sm text-orange-800">
              <Mail className="h-4 w-4 shrink-0" />
              <span>Deja tu email y te enviamos opciones de transportistas</span>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Input
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && isValidEmail) handleSubmit();
                }}
                className="h-8 text-sm w-full sm:w-64 bg-white"
              />
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={isPending || !isValidEmail}
                className="h-8 text-sm shrink-0"
              >
                {isPending ? "Enviando..." : "Confirmar"}
              </Button>
            </div>
            {error && (
              <span className="text-xs text-red-600">{error}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
